// @ts-nocheck
import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { getUser } from "../auth/auth";
import { getAllDisputes, markDisputeInReview, resolveDispute } from "../api/disputes";
import { getUserOrders } from "../api/orders";
import { fetchUserById } from "../api/users";
import { useToast } from "../components/ToastProvider";
import "../styles/AdminDisputes.css";

export default function AdminDisputes() {
  const navigate = useNavigate();
  const user = getUser();
  const { showToast } = useToast();
  
  const [disputes, setDisputes] = useState([]);
  const [filteredDisputes, setFilteredDisputes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [filterStatus, setFilterStatus] = useState("all");
  const [selectedDispute, setSelectedDispute] = useState(null);
  const [disputeDetails, setDisputeDetails] = useState(null);
  const [showDetailPanel, setShowDetailPanel] = useState(false);
  const [showResolveModal, setShowResolveModal] = useState(false);
  const [resolutionForm, setResolutionForm] = useState({
    type: "",
    refundAmount: "",
    adminNotes: ""
  });
  const [resolving, setResolving] = useState(false);

  useEffect(() => {
    if (filterStatus === "all") {
      setFilteredDisputes(disputes);
    } else {
      setFilteredDisputes(disputes.filter((d) => d.status === filterStatus));
    }
  }, [disputes, filterStatus]);

  const fetchDisputes = useCallback(async () => {
    try {
      const data = await getAllDisputes();
      setDisputes(data);
    } catch (err) {
      if (err?.message?.toLowerCase?.().includes("forbidden")) {
        showToast("Unauthorized access", "error");
        navigate("/home");
        return;
      }
      setError("Failed to load disputes");
    } finally {
      setLoading(false);
    }
  }, [navigate, showToast]);

  useEffect(() => {
    if (!user) {
      navigate("/login");
      return;
    }

    if (user.role !== "admin") {
      showToast("Unauthorized access", "error");
      navigate("/home");
      return;
    }

    fetchDisputes();
  }, [user, navigate, fetchDisputes, showToast]);

  const handleViewDetails = async (dispute) => {
    setSelectedDispute(dispute);
    setShowDetailPanel(true);
    
    try {
      // Fetch additional details
      const [buyer, seller, order] = await Promise.all([
        fetchUserById(dispute.buyerId),
        fetchUserById(dispute.sellerId),
        getUserOrders(dispute.buyerId).then(orders => orders.find(o => o.id === dispute.orderId))
      ]);
      
      setDisputeDetails({ buyer, seller, order });
    } catch (err) {
      console.error("Failed to load dispute details:", err);
    }
  };

  const handleResolve = (type) => {
    const orderAmount = disputeDetails?.order?.totalAmount || "";
    setResolutionForm({ 
      type, 
      refundAmount: type === "refund" ? orderAmount : "",
      adminNotes: ""
    });
    setShowResolveModal(true);
  };

  const handleMarkInReview = async () => {
    if (!selectedDispute) return;

    try {
      await markDisputeInReview(selectedDispute.id, "Marked under extended admin review");
      showToast("Dispute marked under review", "success");
      setShowDetailPanel(false);
      setSelectedDispute(null);
      await fetchDisputes();
    } catch (err) {
      showToast(err.message || "Failed to update dispute", "error");
    }
  };

  const handleSubmitResolution = async (e) => {
    e.preventDefault();
    
    if (!resolutionForm.type) {
      showToast("Please select a resolution type", "warning");
      return;
    }

    if (resolutionForm.type !== "release" && !resolutionForm.refundAmount) {
      showToast("Please enter refund amount", "warning");
      return;
    }

    setResolving(true);

    try {
      await resolveDispute(
        selectedDispute.id,
        resolutionForm.type,
        parseFloat(resolutionForm.refundAmount) || 0,
        resolutionForm.adminNotes
      );

      showToast("Dispute resolved successfully!", "success");
      setShowResolveModal(false);
      setShowDetailPanel(false);
      setSelectedDispute(null);
      
      // Refresh disputes
      await fetchDisputes();
    } catch (err) {
      showToast(err.message || "Failed to resolve dispute", "error");
    } finally {
      setResolving(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "opened": return "#fbbf24";
      case "awaiting_seller_response": return "#f97316";
      case "in_review": return "#60a5fa";
      case "resolved_refund": return "#ef4444";
      case "resolved_partial": return "#fb923c";
      case "resolved_release_funds": return "#10b981";
      case "closed": return "#9ca3af";
      default: return "#9ca3af";
    }
  };

  const getStatusLabel = (status) => {
    return status.replace(/_/g, " ").replace(/\b\w/g, l => l.toUpperCase());
  };

  const getTimeRemaining = (deadline) => {
    const now = new Date();
    const end = new Date(deadline);
    const diff = end - now;
    
    if (diff <= 0) return "Expired";
    
    const hours = Math.floor(diff / (1000 * 60 * 60));
    const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
    
    if (hours >= 24) {
      const days = Math.floor(hours / 24);
      return `${days}d ${hours % 24}h`;
    }
    return `${hours}h ${minutes}m`;
  };

  if (loading) {
    return (
      <div className="admin-disputes-page">
        <div className="loading">Loading disputes...</div>
      </div>
    );
  }

  return (
    <div className="admin-disputes-page">
      <div className="admin-container">
        <div className="admin-header">
          <button className="back-btn" onClick={() => navigate("/home")}>
            ← Back
          </button>
          <div>
            <h1>🔒 Admin Dispute Resolution</h1>
            <p>Manage and resolve buyer-seller disputes</p>
          </div>
        </div>

        {error && <div className="error-message">{error}</div>}

        {/* Filter Tabs */}
        <div className="filter-tabs">
          <button 
            className={filterStatus === "all" ? "active" : ""}
            onClick={() => setFilterStatus("all")}
          >
            All ({disputes.length})
          </button>
          <button 
            className={filterStatus === "opened" ? "active" : ""}
            onClick={() => setFilterStatus("opened")}
          >
            Opened ({disputes.filter(d => d.status === "opened").length})
          </button>
          <button 
            className={filterStatus === "awaiting_seller_response" ? "active" : ""}
            onClick={() => setFilterStatus("awaiting_seller_response")}
          >
            Awaiting Response ({disputes.filter(d => d.status === "awaiting_seller_response").length})
          </button>
          <button 
            className={filterStatus === "in_review" ? "active" : ""}
            onClick={() => setFilterStatus("in_review")}
          >
            Under Review ({disputes.filter(d => d.status === "in_review").length})
          </button>
        </div>

        {/* Disputes Table */}
        <div className="disputes-table">
          <div className="table-header">
            <div className="col-id">Dispute ID</div>
            <div className="col-order">Order ID</div>
            <div className="col-type">Issue Type</div>
            <div className="col-status">Status</div>
            <div className="col-date">Filed Date</div>
            <div className="col-deadline">Deadline</div>
            <div className="col-action">Action</div>
          </div>

          {filteredDisputes.length === 0 ? (
            <div className="empty-table">
              No disputes found for this filter
            </div>
          ) : (
            <div className="table-body">
              {filteredDisputes.map((dispute) => (
                <div key={dispute.id} className="table-row">
                  <div className="col-id">{dispute.id}</div>
                  <div className="col-order">{dispute.orderId}</div>
                  <div className="col-type">{dispute.issueType}</div>
                  <div className="col-status">
                    <span 
                      className="status-badge" 
                      style={{ borderColor: getStatusColor(dispute.status), color: getStatusColor(dispute.status) }}
                    >
                      {getStatusLabel(dispute.status)}
                    </span>
                  </div>
                  <div className="col-date">
                    {new Date(dispute.createdAt).toLocaleDateString()}
                  </div>
                  <div className="col-deadline">
                    {dispute.sellerResponseDeadline && !dispute.status.startsWith("resolved_") ? (
                      <span className={new Date(dispute.sellerResponseDeadline) < new Date() ? "deadline-expired" : ""}>
                        {getTimeRemaining(dispute.sellerResponseDeadline)}
                      </span>
                    ) : "—"}
                  </div>
                  <div className="col-action">
                    <button 
                      className="btn-view" 
                      onClick={() => handleViewDetails(dispute)}
                    >
                      View Details
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Detail Panel */}
      {showDetailPanel && selectedDispute && (
        <div className="detail-panel-overlay" onClick={() => setShowDetailPanel(false)}>
          <div className="detail-panel" onClick={(e) => e.stopPropagation()}>
            <button className="panel-close" onClick={() => setShowDetailPanel(false)}>×</button>
            
            <h2 className="panel-title">Dispute Details</h2>
            <div className="panel-subtitle">{selectedDispute.id}</div>

            {/* Order Summary */}
            <div className="panel-section">
              <h3>Order Summary</h3>
              {disputeDetails?.order && (
                <div className="order-summary">
                  <div><strong>Order ID:</strong> {disputeDetails.order.id}</div>
                  <div><strong>Item:</strong> {disputeDetails.order.itemTitle}</div>
                  <div><strong>Amount:</strong> ${disputeDetails.order.totalAmount?.toFixed(2)}</div>
                  <div><strong>Status:</strong> {disputeDetails.order.status}</div>
                </div>
              )}
            </div>

            {/* Buyer & Seller Profiles */}
            <div className="panel-section">
              <h3>Parties Involved</h3>
              <div className="parties-grid">
                {disputeDetails?.buyer && (
                  <div className="party-card">
                    <strong>Buyer</strong>
                    <div>{disputeDetails.buyer.name}</div>
                    <div className="email">{disputeDetails.buyer.email}</div>
                    {disputeDetails.buyer.strikes > 0 && (
                      <div className="strike-warning">⚠️ {disputeDetails.buyer.strikes} strike(s)</div>
                    )}
                  </div>
                )}
                {disputeDetails?.seller && (
                  <div className="party-card">
                    <strong>Seller</strong>
                    <div>{disputeDetails.seller.name}</div>
                    <div className="email">{disputeDetails.seller.email}</div>
                    {disputeDetails.seller.strikes > 0 && (
                      <div className="strike-warning">⚠️ {disputeDetails.seller.strikes} strike(s)</div>
                    )}
                  </div>
                )}
              </div>
            </div>

            {/* Dispute Details */}
            <div className="panel-section">
              <h3>Dispute Information</h3>
              <div className="dispute-info">
                <div><strong>Issue Type:</strong> {selectedDispute.issueType}</div>
                <div><strong>Buyer's Description:</strong></div>
                <p className="description">{selectedDispute.description}</p>
                {selectedDispute.evidence && selectedDispute.evidence.length > 0 && (
                  <div><strong>Buyer's Evidence:</strong> {selectedDispute.evidence.length} photo(s)</div>
                )}
              </div>
            </div>

            {/* Seller Response */}
            {selectedDispute.sellerResponse && (
              <div className="panel-section">
                <h3>Seller Response</h3>
                <div className="seller-response">
                  <p>{selectedDispute.sellerResponse.message}</p>
                  {selectedDispute.sellerResponse.evidence && selectedDispute.sellerResponse.evidence.length > 0 && (
                    <div><strong>Evidence:</strong> {selectedDispute.sellerResponse.evidence.length} photo(s)</div>
                  )}
                  <div className="response-time">
                    Submitted: {new Date(selectedDispute.sellerResponse.submittedAt).toLocaleString()}
                  </div>
                </div>
              </div>
            )}

            {/* Current Status */}
            <div className="panel-section">
              <h3>Current Status</h3>
              <div 
                className="status-display" 
                style={{ borderColor: getStatusColor(selectedDispute.status) }}
              >
                <span className="status-dot" style={{ background: getStatusColor(selectedDispute.status) }}></span>
                {getStatusLabel(selectedDispute.status)}
              </div>
              {selectedDispute.sellerResponseDeadline && !selectedDispute.status.startsWith("resolved_") && (
                <div className="deadline-info">
                  Seller Response Deadline: {getTimeRemaining(selectedDispute.sellerResponseDeadline)}
                </div>
              )}
            </div>

            {/* Admin Actions */}
            {!selectedDispute.status.startsWith("resolved_") && (
              <div className="panel-section">
                <h3>Admin Actions</h3>
                <div className="admin-actions-grid">
                  <button className="btn-resolve refund" onClick={() => handleResolve("refund")}>
                    Resolve: Refund Buyer
                  </button>
                  <button className="btn-resolve release" onClick={() => handleResolve("release")}>
                    Resolve: Release Funds
                  </button>
                  <button className="btn-resolve partial" onClick={() => handleResolve("partial")}>
                    Resolve: Partial Refund
                  </button>
                  <button className="btn-resolve info" onClick={handleMarkInReview}>
                    Mark Under Review
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Resolution Modal */}
      {showResolveModal && (
        <div className="modal-overlay" onClick={() => setShowResolveModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <button className="modal-close" onClick={() => setShowResolveModal(false)}>×</button>
            
            <h2 className="modal-title">Resolve Dispute</h2>
            <p className="modal-subtitle">
              {resolutionForm.type === "refund" && "Full Refund to Buyer"}
              {resolutionForm.type === "release" && "Release Funds to Seller"}
              {resolutionForm.type === "partial" && "Partial Refund"}
            </p>

            <form className="resolution-form" onSubmit={handleSubmitResolution}>
              {resolutionForm.type !== "release" && (
                <div className="form-group">
                  <label htmlFor="refundAmount">
                    Refund Amount ($) *
                  </label>
                  <input
                    type="number"
                    id="refundAmount"
                    step="0.01"
                    value={resolutionForm.refundAmount}
                    onChange={(e) => setResolutionForm({ ...resolutionForm, refundAmount: e.target.value })}
                    placeholder="0.00"
                    required
                  />
                </div>
              )}

              <div className="form-group">
                <label htmlFor="adminNotes">Admin Notes *</label>
                <textarea
                  id="adminNotes"
                  value={resolutionForm.adminNotes}
                  onChange={(e) => setResolutionForm({ ...resolutionForm, adminNotes: e.target.value })}
                  placeholder="Explain the reasoning for this resolution..."
                  rows="4"
                  required
                />
              </div>

              <div className="resolution-warning">
                <strong>This action will:</strong>
                <ul>
                  <li>Update dispute status to resolved</li>
                  <li>Update order status accordingly</li>
                  {(resolutionForm.type === "refund" || resolutionForm.type === "partial") && (
                    <li className="strike-notice">⚠️ Add strike to seller's account</li>
                  )}
                  <li>Send notifications to buyer and seller</li>
                  <li>Log this action in audit trail</li>
                </ul>
              </div>

              <div className="modal-actions">
                <button 
                  type="button" 
                  className="btn-secondary" 
                  onClick={() => setShowResolveModal(false)}
                  disabled={resolving}
                >
                  Cancel
                </button>
                <button 
                  type="submit" 
                  className="btn-primary"
                  disabled={resolving}
                >
                  {resolving ? "Processing..." : "Confirm Resolution"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
